import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllBases,
  getSingleBaseById,
  createBase,
  updateBase,
  deleteBase,
} from "@/api/new-api/staff-management/menu-manage/essential-menu/base";

// Thunks

export const fetchAllBases = createAsyncThunk(
  "bases/fetchAll",
  async ({ restaurant_id, menu_id }, { rejectWithValue }) => {
    const res = await getAllBases(restaurant_id, menu_id);
    if (res.status === "success") return res.data.bases;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleBase = createAsyncThunk(
  "bases/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleBaseById(id);
    if (res.status === "success") return res.data.base;
    return rejectWithValue(res.message);
  }
);

export const createNewBase = createAsyncThunk(
  "bases/create",
  async (data, { rejectWithValue }) => {
    const res = await createBase(data);
    if (res.status === "success") return res.data.base;
    return rejectWithValue(res.message);
  }
);

export const updateExistingBase = createAsyncThunk(
  "bases/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateBase(id, data);
    if (res.status === "success") return res.data.base;
    return rejectWithValue(res.message);
  }
);

export const deleteBaseById = createAsyncThunk(
  "bases/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteBase(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State

const initialState = {
  bases: [],
  singleBase: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const baseSlice = createSlice({
  name: "bases",
  initialState,
  reducers: {
    clearBaseMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllBases.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllBases.fulfilled, (state, action) => {
        state.getLoading = false;
        state.bases = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllBases.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleBase.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleBase.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleBase = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleBase.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewBase.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewBase.fulfilled, (state, action) => {
        state.createLoading = false;
        state.bases.push(action.payload);
        state.success = "Base created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewBase.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingBase.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingBase.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.bases = state.bases.map((base) =>
          base.id === action.payload.id ? action.payload : base
        );
        state.success = "Base updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingBase.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteBaseById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteBaseById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.bases = state.bases.map((item) =>
          item.id === action.payload ? { ...item, status: "archive" } : item
        );
        state.success = "Base deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteBaseById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearBaseMessages } = baseSlice.actions;
export default baseSlice.reducer;
